/* SwarmView — one coordinator run as a living picture: the coordinator in
   the middle, the specialists it named around it, the judge below, and the
   shared notes they hand each other on the right. Draws a swarm doc
   (jobs/{runId}/swarm/{docId}, shape in backend/shared/blocks/swarmFeed.js)
   whether it is live from Firestore or a scripted replay (data/swarmDemo.js).

   It shows what the runtime enforces: the coordinator names roles and
   judges rounds, specialists share notes and ask for other roles, and none
   of them can commit — the gate and the side effect live in the pipeline. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SwarmView.css';

const ROLE_HUE = {
  research: 196, spec: 262, architecture: 282, engineering: 212, qa: 160, security: 2,
  content: 32, pricing: 48, finance: 88, compliance: 330, sales: 20, coordinator: 174,
};
const hue = (role) => ROLE_HUE[role] ?? 200;
const initials = (name = '') => name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const fmtMs = (v) => (v == null ? '' : v < 1000 ? `${v}ms` : v < 60000 ? `${(v / 1000).toFixed(1)}s` : `${Math.floor(v / 60000)}m ${Math.round((v % 60000) / 1000)}s`);
const fmtTok = (v) => (v == null ? '' : v >= 1000 ? `${(v / 1000).toFixed(1)}k tok` : `${v} tok`);
const MODE_LABEL = { parallel: 'parallel', sync: 'in order' };

function useElapsed(doc) {
  const [, tick] = useState(0);
  const live = doc && doc.status === 'running';
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [live]);
  if (!doc?.startedAt) return null;
  return (doc.finishedAt || Date.now()) - doc.startedAt;
}

/* Ring geometry: coordinator hub at the centre, up to 8 specialists on an
   ellipse, the judge at the bottom. viewBox is fixed; CSS scales it. */
const W = 640; const H = 440; const CX = 320; const CY = 200; const RX = 240; const RY = 138;
function slot(i, n) {
  const a = -Math.PI / 2 + (i / Math.max(n, 1)) * Math.PI * 2 + (n === 1 ? 0 : Math.PI / n);
  return { x: CX + RX * Math.cos(a), y: CY + RY * Math.sin(a) };
}

export function Ring({ round, phase, final }) {
  const agents = useMemo(() => round?.agents || [], [round]);
  const pos = useMemo(() => Object.fromEntries(agents.map((a, i) => [a.role, slot(i, agents.length)])), [agents]);
  const judge = round?.judge;
  const hubState = phase === 'planning' ? 'planning' : phase === 'judging' ? 'waiting' : final ? 'done' : 'live';
  return (
    <svg className="sw-ring" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Coordinator, specialists and judge for the current round">
      <defs>
        <radialGradient id="sw-hub-glow"><stop offset="0" stopColor="rgba(20,224,200,.35)" /><stop offset="1" stopColor="rgba(20,224,200,0)" /></radialGradient>
      </defs>
      <ellipse cx={CX} cy={CY} rx={RX} ry={RY} className="sw-orbit" />
      {/* spokes: coordinator → specialist */}
      {agents.map((a) => {
        const p = pos[a.role];
        return <line key={`s-${a.role}`} x1={CX} y1={CY} x2={p.x} y2={p.y} className={`sw-spoke is-${a.status || 'planned'}`} />;
      })}
      {/* suggestions: specialist → specialist it asked for */}
      {agents.filter((a) => a.suggestedBy && pos[a.suggestedBy]).map((a) => {
        const f = pos[a.suggestedBy]; const t = pos[a.role];
        const mx = (f.x + t.x) / 2 + (CY - (f.y + t.y) / 2) * 0.35; const my = (f.y + t.y) / 2 + ((f.x + t.x) / 2 - CX) * 0.35;
        return (
          <g key={`g-${a.role}`}>
            <path d={`M${f.x} ${f.y} Q ${mx} ${my} ${t.x} ${t.y}`} className="sw-suggest" />
            <text x={mx} y={my} className="sw-suggest-label" textAnchor="middle">asked for</text>
          </g>
        );
      })}
      {/* judge */}
      <line x1={CX} y1={CY + 40} x2={CX} y2={H - 58} className={`sw-spoke is-judge ${judge ? `is-${judge.status}` : 'is-idle'}`} />
      <g className={`sw-judge ${judge ? `is-${judge.status}` : 'is-idle'} ${judge?.verdict === 'APPROVED' ? 'is-approved' : judge?.verdict ? 'is-notyet' : ''}`} transform={`translate(${CX} ${H - 40})`}>
        <rect x="-62" y="-16" width="124" height="32" rx="16" />
        <text y="4" textAnchor="middle">{judge?.verdict === 'APPROVED' ? 'Judge: approved' : judge?.verdict ? 'Judge: not yet' : judge?.status === 'running' ? 'Judging…' : 'Judge'}</text>
      </g>
      {/* hub */}
      <g className={`sw-hub is-${hubState}`} transform={`translate(${CX} ${CY})`}>
        <circle r="64" fill="url(#sw-hub-glow)" className="sw-hub-glow" />
        <circle r="40" className="sw-hub-core" />
        <text y="-4" textAnchor="middle" className="sw-hub-title">Coordinator</text>
        <text y="12" textAnchor="middle" className="sw-hub-sub">{round ? `round ${round.n}` : 'idle'}{phase === 'planning' ? ' · planning' : ''}</text>
      </g>
      {/* specialists */}
      {agents.map((a) => {
        const p = pos[a.role];
        const st = a.status || 'planned';
        return (
          <g key={a.role} className={`sw-agent is-${st}`} transform={`translate(${p.x} ${p.y})`} style={{ '--h': hue(a.role) }}>
            <circle r="30" className="sw-agent-pulse" />
            <circle r="24" className="sw-agent-core" />
            <text y="5" textAnchor="middle" className="sw-agent-ini">{initials(a.name || a.role)}</text>
            <text y="40" textAnchor="middle" className="sw-agent-name">{a.name || a.role}</text>
            <text y="53" textAnchor="middle" className="sw-agent-meta">
              {st === 'done' ? `${fmtMs(a.ms)} · ${fmtTok(a.tokens)}` : st === 'running' ? 'working…' : st === 'retrying' ? 'retrying' : st === 'failed' ? 'failed' : a.suggestedBy ? 'suggested' : MODE_LABEL[a.mode] || a.mode}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function Ledger({ doc, compact }) {
  const ref = useRef(null);
  const count = (doc?.rounds || []).reduce((n, r) => n + r.agents.filter((a) => a.status === 'done' || a.status === 'failed').length + (r.judge?.status === 'done' ? 1 : 0), 0);
  useEffect(() => {
    const el = ref.current;
    if (el && doc?.status === 'running') el.scrollTop = el.scrollHeight;
  }, [count, doc?.status]);
  return (
    <div className="sw-ledger" ref={ref}>
      <div className="sw-ledger-title">Shared notes</div>
      {!doc?.rounds?.length && <p className="sw-empty">Waiting for the coordinator to plan the first round.</p>}
      {(doc?.rounds || []).map((r) => (
        <section key={r.n} className="sw-round">
          <header>
            <span>Round {r.n}</span>
            {r.plan?.status === 'done' && <em>{r.agents.length} roles · planned in {fmtMs(r.plan.ms)}</em>}
            {r.plan?.status === 'failed' && <em className="is-bad">plan failed</em>}
          </header>
          {r.agents.filter((a) => a.status === 'done' || a.status === 'failed' || (!compact && a.status === 'running')).map((a) => (
            <article key={a.role} className={`sw-note is-${a.status}`} style={{ '--h': hue(a.role) }}>
              <div className="sw-note-head">
                <i className="sw-dot" />
                <strong>{a.name || a.role}</strong>
                {a.suggestedBy && <span className="sw-chip">asked for by {a.suggestedBy}</span>}
                {a.status === 'done' && <span className="sw-note-meta">{fmtMs(a.ms)} · {fmtTok(a.tokens)}</span>}
              </div>
              {a.status === 'running' && <p className="sw-note-task">{a.task}</p>}
              {a.status === 'done' && <p>{a.preview}</p>}
              {a.status === 'failed' && <p className="is-bad">Failed after one retry{a.error ? `: ${a.error}` : ''}.</p>}
              {a.status === 'done' && a.suggest?.length > 0 && (
                <div className="sw-note-sugg">asks for {a.suggest.join(', ')}</div>
              )}
            </article>
          ))}
          {r.judge?.status === 'done' && (
            <div className={`sw-verdict ${r.judge.verdict === 'APPROVED' ? 'is-approved' : 'is-notyet'}`}>
              <strong>{r.judge.verdict === 'APPROVED' ? 'Approved' : 'Not yet'}</strong>
              {r.judge.note ? <span> — {r.judge.note}</span> : null}
            </div>
          )}
        </section>
      ))}
      {doc?.stop && (
        <div className={`sw-stop is-${doc.status}`}>
          Stopped because {doc.why}. {doc.rounds?.length || 0} round{doc.rounds?.length === 1 ? '' : 's'} · {fmtTok(doc.tokens)} · {fmtMs(doc.ms)}.
          <em> The result now goes to the gate — this swarm cannot commit.</em>
        </div>
      )}
    </div>
  );
}

export default function SwarmView({ doc, title, subtitle, tag, compact = false, followLive = true }) {
  const rounds = doc?.rounds || [];
  const liveRound = doc?.round || rounds[rounds.length - 1]?.n || null;
  const [picked, setPicked] = useState(null);
  useEffect(() => { if (followLive) setPicked(null); }, [liveRound, followLive]);
  const shownN = picked ?? liveRound;
  const round = rounds.find((r) => r.n === shownN) || null;
  const elapsed = useElapsed(doc);

  let phase = 'idle';
  if (doc?.status === 'running' && round && shownN === liveRound) {
    if (!round.plan) phase = 'planning';
    else if (round.judge?.status === 'running') phase = 'judging';
    else phase = 'working';
  }
  const final = doc && doc.status !== 'running';
  const status = !doc ? 'idle' : doc.status;
  const statusLabel = { running: 'running', approved: 'objective met', stopped: 'stopped', failed: 'failed', idle: 'idle' }[status] || status;
  const activeCount = round ? round.agents.filter((a) => a.status === 'running' || a.status === 'retrying').length : 0;

  return (
    <div className={`sw ${compact ? 'sw-compact' : ''}`}>
      <div className="sw-head">
        <div className="sw-head-main">
          {tag && <span className="sw-tag">{tag}</span>}
          <strong>{title || doc?.label || 'Coordinator'}</strong>
          {subtitle && <span className="sw-sub">{subtitle}</span>}
        </div>
        <div className="sw-head-stats">
          <span className={`sw-status is-${status}`}><i />{statusLabel}</span>
          {doc?.config && <span>{rounds.length}/{doc.config.maxRounds} rounds</span>}
          {doc?.config && <span>cap {doc.config.maxAgents} roles</span>}
          {doc && <span>{fmtTok(doc.tokens || 0)}</span>}
          {elapsed != null && <span>{fmtMs(elapsed)}</span>}
        </div>
      </div>
      {doc?.objective && <p className="sw-objective"><span>Objective</span>{doc.objective}</p>}
      <div className="sw-body">
        <div className="sw-stage">
          <Ring round={round} phase={phase} final={final} />
          <div className="sw-rounds">
            {rounds.map((r) => (
              <button
                key={r.n}
                type="button"
                className={`sw-round-chip ${r.n === shownN ? 'is-current' : ''} ${r.judge?.verdict === 'APPROVED' ? 'is-approved' : r.judge?.verdict ? 'is-notyet' : ''}`}
                onClick={() => setPicked(r.n === liveRound ? null : r.n)}
              >
                R{r.n}
              </button>
            ))}
            {doc?.config && rounds.length < doc.config.maxRounds && status === 'running' && Array.from({ length: doc.config.maxRounds - rounds.length }).map((_, i) => (
              <span key={`f-${i}`} className="sw-round-chip is-future">R{rounds.length + i + 1}</span>
            ))}
            {activeCount > 0 && <span className="sw-active">{activeCount} working now</span>}
          </div>
        </div>
        <Ledger doc={doc} compact={compact} />
      </div>
    </div>
  );
}
